/**
 * What the operator's console shows inside the app (admin-dashboard §4.10):
 * announcement banners, the warning before maintenance, and the calm
 * maintenance screen, and (Console v2) the calm "Safety mode" line while
 * raid mode runs. All of it comes from the server's config
 * (`store/config.ts`); nothing here is guessed.
 *
 * The banners take a row of their own (under the title bar in the app,
 * above the sign-in card when signed out) and push the rest down; they
 * never cover the app (QA SN-B01).
 */
import type { LocalizedText } from '@neosocial/protocol';
import { useEffect, useRef, useState } from 'react';
import markUrl from '../assets/mark.svg';
import { useT } from '../i18n';
import {
  dismissAnnouncement,
  loadConfig,
  useConfig,
  useMaintenancePhase,
  visibleAnnouncements,
} from '../store/config';
import { signOut } from '../store/session';
import { Btn, Icon, IconBtn } from '../ui/kit';
import { DesktopDownloadBar, useDesktopDownload } from './DesktopDownloadBar';

/** A console text; empty when the console left it empty. */
export function localized(t: LocalizedText | null | undefined): string {
  return t?.en ?? '';
}

/**
 * The time, refreshed every `ms` and also at the next of `moments` (epoch
 * ms) still ahead of it. An announcement published just now starts at the
 * server's "now", which is after this clock's last tick: without the wake-up
 * it stayed hidden until the next tick, up to `ms` later.
 */
function useNow(ms: number, moments: number[]): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  const next = moments.reduce((m, t) => (t > now && t < m ? t : m), Infinity);
  useEffect(() => {
    if (next === Infinity) return;
    // a delay past 2^31 ms would fire at once; the interval covers anything that far
    const id = setTimeout(() => setNow(Date.now()), Math.min(Math.max(0, next - Date.now()), ms));
    return () => clearTimeout(id);
  }, [next, ms]);
  return now;
}

/** Every start and end of the announcements (epoch ms). */
function announcementMoments(list: { starts_at: string; ends_at: string | null }[]): number[] {
  return list.flatMap((a) => [Date.parse(a.starts_at), a.ends_at ? Date.parse(a.ends_at) : NaN]);
}

/** After a banner goes: the next banner's close button, else the previous one's, else the page. */
function focusAfterDismiss(box: HTMLElement | null, btn: HTMLElement): void {
  const xs = [...(box?.querySelectorAll<HTMLElement>('.ns-cfg-x') ?? [])];
  const i = xs.indexOf(btn);
  const next = xs[i + 1] ?? xs[i - 1];
  if (next) return next.focus({ preventScroll: true });
  const main =
    document.querySelector<HTMLElement>('#nx-main') ??
    document.querySelector<HTMLElement>('.ns-auth-card') ??
    document.querySelector<HTMLElement>('main');
  if (!main) return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  main.focus({ preventScroll: true });
}

/** Raid mode while it runs (Console v2 decision 9), from the config; null otherwise. */
export function raidNow(
  config:
    { raid?: { active: true; ends_at: string; slowmode_s: number } | null } | null | undefined,
  now = Date.now(),
): { ends_at: string; slowmode_s: number } | null {
  const r = config?.raid;
  return r?.active && Date.parse(r.ends_at) > now ? r : null;
}

/**
 * Banners: for staff while maintenance runs (the app stays open for them),
 * the maintenance warning, the calm safety-mode line while raid mode runs,
 * then announcements, newest first, then (web on Windows only) the desktop
 * app's download offer.
 */
export function ConfigBanners() {
  const { t, fmt } = useT();
  const box = useRef<HTMLDivElement>(null);
  const config = useConfig((s) => s.config);
  const now = useNow(30_000, [
    ...announcementMoments(config?.announcements ?? []),
    // it goes by itself at its end
    ...(config?.raid?.ends_at ? [Date.parse(config.raid.ends_at)] : []),
  ]);
  const dismissed = useConfig((s) => s.dismissed);
  const phase = useMaintenancePhase();
  const download = useDesktopDownload();
  const list = visibleAnnouncements({ config, dismissed }, now);
  const starts = config?.maintenance.starts_at;
  const staffInMaintenance = phase === 'active' && !!config?.maintenance.exempt;
  const raid = raidNow(config, now);
  if (!list.length && phase !== 'warning' && !staffInMaintenance && !download.release && !raid)
    return null;
  return (
    <div className="ns-cfg-bars" ref={box} role="region" aria-label={t('cfg.ann.label')}>
      {staffInMaintenance ? (
        <div className="ns-cfg-bar nx-raised" data-style="warning" role="status">
          <Icon name="moon" />
          <div className="ns-cfg-bar-t">
            <b>{t('cfg.mt.staff')}</b>
          </div>
        </div>
      ) : null}
      {phase === 'warning' && starts ? (
        <div className="ns-cfg-bar nx-raised" data-style="warning" role="alert">
          <Icon name="alert" />
          <div className="ns-cfg-bar-t">
            <b>{t('cfg.mt.warn', { time: fmt.time(starts) })}</b>
          </div>
        </div>
      ) : null}
      {raid ? (
        <div className="ns-cfg-bar nx-raised" data-raid role="status">
          <Icon name="shield" />
          <div className="ns-cfg-bar-t">
            <b>{t('gov.safety.on')}</b>
            <span>{t('gov.safety.body', { time: fmt.time(raid.ends_at) })}</span>
          </div>
        </div>
      ) : null}
      {list.map((a) => {
        const title = localized(a.title);
        return (
          <div
            key={a.id}
            className="ns-cfg-bar nx-raised"
            data-style={a.style}
            data-ann={a.id}
            role={a.style === 'warning' ? 'alert' : 'status'}
          >
            <Icon name={a.style === 'warning' ? 'alert' : 'info'} />
            <div className="ns-cfg-bar-t">
              {title ? <b dir="auto">{title}</b> : null}
              <span dir="auto">{localized(a.body)}</span>
            </div>
            {a.dismissible ? (
              <IconBtn
                icon="x"
                className="ns-cfg-x"
                label={t('cfg.ann.dismiss')}
                onClick={(e) => {
                  focusAfterDismiss(box.current, e.currentTarget);
                  dismissAnnouncement(a.id);
                }}
              />
            ) : null}
          </div>
        );
      })}
      {download.release ? (
        <DesktopDownloadBar
          release={download.release}
          onDismiss={(btn) => {
            focusAfterDismiss(box.current, btn);
            download.dismiss();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The calm screen while maintenance runs. It checks again by itself every
 * minute. Signed in, it offers Sign out (the server keeps sign-out open);
 * signed out, staff can still sign in (the server lets them through).
 */
export function MaintenanceScreen({
  signedIn = false,
  onStaffSignIn,
}: {
  signedIn?: boolean;
  onStaffSignIn?: () => void;
}) {
  const { t, fmt } = useT();
  const config = useConfig((s) => s.config);
  const [checking, setChecking] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [checked, setChecked] = useState(() => Date.now());
  const check = async () => {
    setChecking(true);
    await loadConfig({ force: true });
    setChecked(Date.now());
    setChecking(false);
  };
  useEffect(() => {
    const id = setInterval(() => void check(), 60_000);
    return () => clearInterval(id);
  }, []);
  const m = config?.maintenance;
  const message =
    localized(m?.message) || localized(config?.settings['texts.maintenance']) || t('cfg.mt.title');
  return (
    <div className="ns-splash ns-maint" role="main">
      <div className="nx-sky" aria-hidden="true">
        <i className="aur aur-hub" />
        <i className="grain" />
      </div>

      <div className="ns-maint-card nx-raised">
        <img src={markUrl} alt="" width={48} height={48} className="ns-splash-mark" />
        <Icon name="moon" />
        <h1 dir="auto">{message}</h1>
        <p>{t('cfg.mt.body')}</p>
        <p className="ns-maint-when">
          {m?.ends_at
            ? t('cfg.mt.until', { date: fmt.date(m.ends_at), time: fmt.time(m.ends_at) })
            : t('cfg.mt.soon')}
        </p>
        <div className="ns-row-btns ns-maint-acts">
          <Btn kind="ghost" size="sm" busy={checking} onClick={() => void check()}>
            {checking ? t('cfg.mt.checking') : t('cfg.mt.retry')}
          </Btn>
          {signedIn ? (
            <Btn
              kind="ghost"
              size="sm"
              busy={leaving}
              onClick={() => {
                setLeaving(true);
                void signOut().finally(() => setLeaving(false));
              }}
            >
              {t('ns.signout')}
            </Btn>
          ) : onStaffSignIn ? (
            <Btn kind="ghost" size="sm" onClick={onStaffSignIn}>
              {t('cfg.mt.staffSignIn')}
            </Btn>
          ) : null}
        </div>
        <span className="ns-muted">{t('cfg.mt.checked', { time: fmt.time(checked) })}</span>
      </div>
    </div>
  );
}
